import axios, { AxiosInstance } from 'axios';
import { TopicAnalysisRequest, TopicAnalysisResponse } from '../types';

// Use proxy in development, direct URL in production
// Check if we're in development mode (localhost or development server)
console.log('process.env.NODE_ENV',process.env.NODE_ENV)
const isDevelopment = process.env.NODE_ENV === 'development' || 
                     window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1' ||
                     window.location.port === '3000' ||
                     window.location.port === '3001';

// For now, always use the main API server as a proxy to avoid CORS issues
// The main API server should proxy requests to the topic analysis API
const TOPIC_ANALYSIS_BASE_URL = process.env.REACT_APP_API_URL || 'https://bassist.meander.one/api';
console.log('TOPIC_ANALYSIS_BASE_URL',TOPIC_ANALYSIS_BASE_URL)
// Create a completely separate axios instance for topic analysis API
const topicAnalysisApi: AxiosInstance = axios.create({
  baseURL: TOPIC_ANALYSIS_BASE_URL,
  timeout: 30000, // Longer timeout for analysis
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'Access-Control-Allow-Origin': '*',
    'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS',
    'Access-Control-Allow-Headers': 'Content-Type, Authorization',
  },
});

// Add request interceptor for debugging (only in development)
if (isDevelopment) {
  topicAnalysisApi.interceptors.request.use(
    (config) => {
      console.log('Topic Analysis API Request:', {
        url: config.url,
        baseURL: config.baseURL,
        fullURL: `${config.baseURL}${config.url}`,
        method: config.method,
        isDevelopment: isDevelopment,
        nodeEnv: process.env.NODE_ENV,
        hostname: window.location.hostname,
        port: window.location.port,
      });
      return config;
    },
    (error) => {
      console.error('Topic Analysis API Request Error:', error);
      return Promise.reject(error);
    }
  );

  topicAnalysisApi.interceptors.response.use(
    (response) => {
      console.log('Topic Analysis API Response:', response.status);
      return response;
    },
    (error) => {
      console.error('Topic Analysis API Response Error:', error);
      return Promise.reject(error);
    }
  );
}

export const topicAnalysisService = {
  // Analyze a topic and get insights with charts
  analyzeTopic: async (request: TopicAnalysisRequest): Promise<TopicAnalysisResponse> => {
    try {
      // Try the main API server first (which should proxy to topic analysis API)
      const response = await topicAnalysisApi.post('/api/topic-analysis/analyze', request);
      return response.data;
    } catch (error: any) {
      console.error('Topic analysis error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error config:', error.config);
      
      // If the main API server doesn't have the topic analysis endpoint,
      // try connecting directly to the topic analysis API as a fallback
      if (error.response?.status === 404) {
        console.log('Main API server does not have topic analysis endpoint, trying direct connection...');
        try {
          // Create a new axios instance for direct connection
          const directApi = axios.create({
            baseURL: 'https://bassist.meander.one/api',
            timeout: 30000,
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
            },
          });
          
          const directResponse = await directApi.post('/api/topic-analysis/analyze', request);
          return directResponse.data;
        } catch (directError: any) {
          console.error('Direct topic analysis API error:', directError);
          // Fall through to the original error handling
        }
      }
      
      // Provide more specific error messages
      if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
        const baseUrl = TOPIC_ANALYSIS_BASE_URL || 'proxy';
        throw new Error(`Network Error: Unable to connect to topic analysis API at ${baseUrl}. Please check if the server is running and accessible. (Development mode: ${isDevelopment})`);
      } else if (error.response?.status === 0) {
        throw new Error(`CORS Error: The topic analysis API is not allowing requests from this domain. Please configure CORS on the server to allow requests from ${window.location.origin}`);
      } else if (error.response?.status) {
        const errorData = error.response.data;
        if (errorData && errorData.message) {
          throw new Error(`API Error (${error.response.status}): ${errorData.message}`);
        } else {
          throw new Error(`API Error (${error.response.status}): ${error.response.statusText}`);
        }
      } else {
        throw new Error(`Unknown Error: ${error.message}`);
      }
    }
  },
};

export default topicAnalysisService;
